import { Box, Stack, type Theme, useTheme } from "@mui/material";
import feedbackNoData from "assets/images/nodata/feedbackNoData.png";
import { GaugeChart } from "components/charts";
import { useGetTicketFeedbackStats, useGlobalStore } from "controller";
import { useAtomValue } from "jotai";
import type { TicketFeedbackStats } from "shared";
import { useDashboardScope } from "../../../hooks/useDashboardScope";
import { activityPeriodAtom } from "../../../store";
import { ActivityCard } from "./ActivityCard";
import { ActivityEmptyState } from "./ActivityEmptyState";
import { ActivityLegend } from "./ActivityLegend";

/**
 * Rating buckets keyed by the labels the report API returns, best-first — the
 * gauge sweeps in this order. The API's "Unrated" bucket is left out: the gauge
 * shows feedback actually received, not total ticket volume.
 */
const getRatings = (theme: Theme) => [
  {
    label: "Awesome",
    color: theme.palette.primitive.system.green[700],
  },
  {
    label: "Good",
    color: theme.palette.primitive.system.green[500],
  },
  {
    label: "Satisfied",
    color: theme.palette.primitive.system.yellow[700],
  },
  {
    label: "Need to improve",
    color: theme.palette.primitive.system.orange[700],
  },
  {
    label: "Disappointed",
    color: theme.palette.primitive.system.red[800],
  },
];

export const FeedbackReceivedCard = () => {
  const theme = useTheme();
  const timezone = useGlobalStore((state) => state.timezone);
  const period = useAtomValue(activityPeriodAtom);
  const { reportResource, isReady } = useDashboardScope();

  // Held until the scope resolves — an empty `resource` reads as tenant-wide, so
  // firing early would flash numbers for the whole MSP.
  const { data, isFetching } = useGetTicketFeedbackStats(
    {
      timePeriod: period,
      timezone,
      resource: reportResource,
    },
    isReady,
  );

  const stats = data as TicketFeedbackStats | undefined;

  const segments = getRatings(theme).map(({ label, color }) => ({
    label,
    color,
    value: stats?.feedback?.[label] ?? 0,
  }));

  const feedbackTotal = segments.reduce(
    (sum, segment) => sum + segment.value,
    0,
  );
  // Only buckets the period actually got — listing all five turns the legend
  // into a static key instead of a read of the data.
  const legendItems = segments
    .filter((segment) => segment.value > 0)
    .map(({ label, color }) => ({ label, color }));

  // Held until the scope resolves.
  const isEmpty = !isFetching && isReady && feedbackTotal === 0;

  if (isEmpty) {
    return (
      <ActivityCard title="Feedback received">
        <ActivityEmptyState
          image={feedbackNoData}
          title="No feedback yet"
          description="No customer feedback has been received."
          imageWidth="98px"
        />
      </ActivityCard>
    );
  }

  return (
    <ActivityCard title="Feedback received">
      <Stack
        sx={{
          alignItems: "center",
          width: "100%",
          height: "100%",
        }}
      >
        {/* The gauge is a half-donut, so its box has to be 2:1 — a taller box
            just leaves dead space above the arc. */}
        <Box
          sx={{
            width: "148px",
            aspectRatio: "2 / 1",
            display: "flex",
          }}
        >
          <GaugeChart
            data={feedbackTotal > 0 ? segments : []}
            labelKey="label"
            valueKey="value"
            width="100%"
            height="100%"
            thickness={18}
            centerContent={String(feedbackTotal)}
            centerSubContent={feedbackTotal > 1 ? "Feedbacks" : "Feedback"}
            isLoading={isFetching}
          />
        </Box>
      </Stack>

      <ActivityLegend items={legendItems} />
    </ActivityCard>
  );
};
